/**
 * usePlaces — the Program Places module composable (Places/Program-Modules epic,
 * Wave 3). Dark behind PROGRAM_MODULES + PLACES_V1.
 *
 * Wraps the Nexus GraphQL places operations behind a thin, typed API:
 *   - loadPlacesModuleState(programId)  → server-derived nudge state (pass-through)
 *   - loadMyPlaceAttendance(programId)  → attendance ledger + program places + policy
 *   - checkIn(programId, placeId, note?) / checkOut(programId, placeId)
 *
 * Every loader SILENT-DEGRADES to a disabled ({ enabled: false }) payload on any
 * error, so a missing/failing backend never breaks the host — the module simply
 * renders its empty state.
 *
 * Also exposes `registerDescriptor()` — the seam that attaches the Places module's
 * panel / policyLoader / nudge / onboardingPhase hooks onto the 'places' descriptor
 * key. Must run inside a Vue setup context (it grabs the Nexus Apollo client). The
 * host (TimelineBar) calls it behind PROGRAM_MODULES.
 *
 * Follows the same client-access pattern as useCoaching: useNexusClient() + inline
 * gql. Generated types are intentionally out of scope here.
 */

import type { Component } from 'vue';
import { gql } from 'graphql-tag';
import { useNexusClient } from '@/composables/useApolloClients';
import { registerModuleDescriptor, type ModulePolicy } from '@/composables/useProgramModules';
import {
  PLACES_MODULE_STATE_QUERY,
  MY_PLACE_ATTENDANCE_QUERY,
  PLACES_ATTENDANCE_SUMMARY_QUERY,
  MY_PLACES_CONTRACT_QUERY,
  PLACES_PRESENCE_ROSTER_QUERY,
  PLACE_CHECK_IN_MUTATION,
  PLACE_CHECK_OUT_MUTATION,
  SET_PLACES_POLICY_MUTATION,
  SET_PLACES_PLEDGE_MUTATION,
  SWITCH_TO_REMOTE_MUTATION,
  SYNC_MY_PLACES_CALENDAR_MUTATION,
  REPORT_MISSED_SESSION_MUTATION,
  SET_PLACE_PRESENCE_VISIBILITY_MUTATION,
  PROPAGATE_PLACES_CHANGE_MUTATION,
  type PlacesModuleState,
  type MyPlaceAttendance,
  type PlacesPolicy,
  type SetPlacesPolicyInput,
  type PropagatePlacesChangeResult,
  type PlacesContractSummary,
  type MyPlacesContract,
  type MyPlacesSchedulePreview,
  type PlacesPledgeMode,
  type PlacesPledgeShape,
  type PlacesPledgeSessionInput,
  type SetPlacesPledgeEnvelope,
  type SwitchToRemoteResult,
  type SyncPlacesCalendarResult,
  type PlacesPresenceRoster,
  type ReportMissedStatus,
  type ReportMissedSessionResult,
  type SetPlacePresenceVisibilityResult
} from '@/services/graphql/nexus/places';

/**
 * The schedule-preview query, EXACT-aware. Declared locally (rather than in the shared
 * `places.ts` op module) so the participant pledge phase can pass the new `exact` arg without
 * touching the shared query:
 *   - `exact` omitted / false → AUTO-DRAFT (greedily fills to the weekly target; seed the grid once).
 *   - `exact: true` → EXACT (every painted in-window cell becomes a session, never trimmed to target).
 * Returns the GraphQLJSON scalar (no sub-selection), typed by `PlacesSchedulePreviewResult`.
 */
const SCHEDULE_PREVIEW_EXACT_QUERY = gql`
  query MyPlacesSchedulePreview($programId: String!, $availability: JSON, $exact: Boolean) {
    myPlacesSchedulePreview(programId: $programId, availability: $availability, exact: $exact)
  }
`;

// ============================================================
// Degraded defaults (silent-degrade targets)
// ============================================================

const DISABLED_POLICY: PlacesPolicy = { enabled: false };

const DISABLED_MODULE_STATE: PlacesModuleState = {
  enabled: false,
  policy: DISABLED_POLICY,
  state: 'idle',
  owedWindow: null,
  blocked: false,
  owedSessions: []
};

const DISABLED_ATTENDANCE: MyPlaceAttendance = {
  enabled: false,
  attendance: [],
  places: [],
  policy: DISABLED_POLICY
};

/** Empty contract silent-degrade target (no pledge yet / disabled backend). */
const EMPTY_CONTRACT: MyPlacesContract = {
  contract: null,
  pledge: null
};

/**
 * The schedule-preview result the pledge UI consumes — the shared service payload PLUS the
 * timezone / term-window fields the `myPlacesSchedulePreview` JSON now carries. Declared here
 * (rather than mutating the shared service interface) so the participant pledge can render +
 * LABEL session times in the program's CANONICAL zone instead of the viewer's browser zone.
 *
 * The query returns the GraphQLJSON scalar (no sub-selection), so these fields already ride on
 * the payload at runtime — this only teaches TypeScript about them.
 */
export interface PlacesSchedulePreviewResult extends MyPlacesSchedulePreview {
  /** IANA zone the session times are anchored to (e.g. 'America/Los_Angeles'). */
  siteTimeZone: string | null;
  /** ISO — start of the window this schedule is valid for (synced to the SponsorLink/program). */
  termStart: string | null;
  /** ISO — end of that window. */
  termEnd: string | null;
  /** ISO — the first actually-applicable day. */
  appliesFrom: string | null;
  /**
   * The creator's OPEN on-site window — the days + hours the participant can paint their
   * commitment across. `days` are 0=Sun … 6=Sat; `startHm`/`endHm` are "HH:MM" 24h. Drives the
   * paint grid's columns (open days) and rows (hour slots) in the schedule-first pledge.
   */
  siteWindow?: { days: number[]; startHm: string; endHm: string } | null;
  /** Whole weeks the term spans — the pledge paginates the dated list by week + shows a term total. */
  weekCount?: number | null;
  /**
   * The real-world in-person details surfaced to the participant on the pledge step (all resolved
   * from the creator's Places policy). Any field may be null/empty — the UI hides a row it has no
   * data for.
   */
  logistics?: PlacesLogistics | null;
}

/** Participant-facing in-person logistics (where / who / what / how). */
export interface PlacesLogistics {
  /** Full site address label (name + street, deduped). */
  address: string | null;
  /** A Google Maps "search" link for `address` (directions). */
  mapsUrl: string | null;
  /** On-site contact — who to ask for on arrival. */
  contact: { name: string | null; phone: string | null } | null;
  /** On-site supervisor (when distinct from the contact). */
  supervisor: { name: string | null; phone: string | null } | null;
  /** Free-text arrival instructions / what to bring (the creator's onSiteAim copy). */
  arrivalInstructions: string | null;
  /** How presence is confirmed + whether check-in is required. */
  checkIn: { method: string | null; required: boolean } | null;
  /** Shortfall consequences at attendance thresholds, e.g. "<60% → flagged". */
  consequences: Array<{ atPct: number | null; consequence: string | null }>;
  /** 'soft' (nudge) | 'hard' (enforced). */
  rigidity: string | null;
}

/** Disabled schedule-preview silent-degrade target (flag-off / failing backend). */
const DISABLED_PREVIEW: PlacesSchedulePreviewResult = {
  enabled: false,
  sessions: [],
  targetType: null,
  targetValue: null,
  targetHours: null,
  committedHours: null,
  meetsTarget: true,
  shortfallBlock: false,
  attendanceMode: 'remote_allowed',
  pledgeRequired: false,
  allowPledgeLater: true,
  siteLocation: null,
  siteTimeZone: null,
  termStart: null,
  termEnd: null,
  appliesFrom: null,
  siteWindow: null,
  weekCount: null,
  logistics: null
};

/**
 * Disabled "who's here" roster silent-degrade target (PW6). A failing/absent presence
 * backend must render NOTHING precise — the "who's here" strip hides itself on `enabled:false`.
 */
const DISABLED_PRESENCE: PlacesPresenceRoster = {
  enabled: false,
  here: [],
  recentCount: 0,
  total: 0
};

/** Creator-lens rollup silent-degrade target (empty cohort, disabled). */
const DISABLED_SUMMARY: PlacesContractSummary = {
  enabled: false,
  target: null,
  place: null,
  rows: [],
  cohort: {
    count: 0,
    avgPledgeHours: null,
    avgActualHours: null,
    pctPledgeMet: null,
    onTrack: 0,
    behind: 0,
    unset: 0,
    remote: 0
  }
};

// ============================================================
// Pure mapper (no Apollo / no component context required)
// ============================================================

/**
 * Fold a PlacesModuleState into the framework's open-ended ModulePolicy so the
 * shared trigger runner can read enabled/required/rigidity/inAppNudge PLUS the
 * server-derived `state` + `owedWindow` (nudge.deriveState is a pass-through of
 * `state`). Mirrors the CoachingPolicy → ModulePolicy shape.
 */
export function mapStateToPolicy(moduleState: PlacesModuleState | null | undefined): ModulePolicy {
  const s = moduleState ?? DISABLED_MODULE_STATE;
  const p = s.policy ?? DISABLED_POLICY;
  return {
    enabled: !!s.enabled && p.enabled !== false,
    required: p.required,
    rigidity: p.rigidity,
    inAppNudge: p.inAppNudge,
    checkInRequired: p.checkInRequired,
    schedule: p.schedule,
    // Folded server-derived fields (deriveState reads `state`).
    state: s.state,
    owedWindow: s.owedWindow
  };
}

// ============================================================
// Composable
// ============================================================

export function usePlaces(providedClient?: ReturnType<typeof useNexusClient>) {
  // Accept a pre-resolved client so callers reached from an ASYNC context (e.g.
  // useAIPMOnboarding.getPlaces runs after awaits, outside component setup) can pass the
  // client they cached synchronously. Calling useNexusClient() here in that async context
  // throws "Apollo client with id nexus not found" (inject() has no active instance). Only
  // fall back to resolving fresh when called directly inside a component setup.
  const client = providedClient ?? useNexusClient();

  async function loadPlacesModuleState(programId: string): Promise<PlacesModuleState> {
    if (!programId) return DISABLED_MODULE_STATE;
    try {
      const { data } = await client.query({
        query: PLACES_MODULE_STATE_QUERY,
        variables: { programId },
        fetchPolicy: 'network-only'
      });
      return (data?.placesModuleState as PlacesModuleState) ?? DISABLED_MODULE_STATE;
    } catch {
      // Silent-degrade: a failing/absent backend must never break the host.
      return DISABLED_MODULE_STATE;
    }
  }

  async function loadMyPlaceAttendance(programId: string): Promise<MyPlaceAttendance> {
    if (!programId) return DISABLED_ATTENDANCE;
    try {
      const { data } = await client.query({
        query: MY_PLACE_ATTENDANCE_QUERY,
        variables: { programId },
        fetchPolicy: 'network-only'
      });
      return (data?.myPlaceAttendance as MyPlaceAttendance) ?? DISABLED_ATTENDANCE;
    } catch {
      return DISABLED_ATTENDANCE;
    }
  }

  /**
   * placesAttendanceSummary(programId) — the CREATOR-lens rollup. Silent-degrades to an
   * empty summary so the dashboard renders graceful-empty when the BE resolver is absent
   * (flagged BE gap for PW7).
   */
  async function loadPlacesAttendanceSummary(programId: string): Promise<PlacesContractSummary> {
    if (!programId) return DISABLED_SUMMARY;
    try {
      const { data } = await client.query({
        query: PLACES_ATTENDANCE_SUMMARY_QUERY,
        variables: { programId },
        fetchPolicy: 'network-only'
      });
      return (data?.placesAttendanceSummary as PlacesContractSummary) ?? DISABLED_SUMMARY;
    } catch {
      return DISABLED_SUMMARY;
    }
  }

  /**
   * myPlacesContract(programId) — the participant's resolved on-site contract + raw
   * pledge (PW5). Silent-degrades to an empty ({ contract:null, pledge:null }) payload so
   * a missing backend / no-pledge-yet renders the onboarding "commit" state.
   */
  async function loadMyPlacesContract(programId: string): Promise<MyPlacesContract> {
    if (!programId) return EMPTY_CONTRACT;
    try {
      const { data } = await client.query({
        query: MY_PLACES_CONTRACT_QUERY,
        variables: { programId },
        fetchPolicy: 'network-only'
      });
      return (data?.myPlacesContract as MyPlacesContract) ?? EMPTY_CONTRACT;
    } catch {
      return EMPTY_CONTRACT;
    }
  }

  /**
   * myPlacesSchedulePreview(programId, availability?) — the SCHEDULE-FIRST preview (PW5
   * redesign). Returns the concrete generated day-by-day session list the participant edits
   * + confirms, plus the target/committed rollup and the attendance policy. `availability`
   * (the participant's StudentPortfolio.intake.availability weekly grid) SEEDS the schedule.
   *
   * `exact` selects the materialization mode:
   *   - omitted / false → AUTO-DRAFT: greedily fills to the weekly target. Use ONCE on first load
   *     to seed the paint grid.
   *   - true → EXACT: every painted in-window cell becomes a session, NEVER trimmed to the target.
   *     Use on every re-materialize after the participant paints their grid.
   *
   * SILENT-DEGRADES to a disabled ({ enabled:false, sessions:[] }) payload so a missing/failing
   * backend renders the graceful-empty state.
   */
  async function schedulePreview(
    programId: string,
    availability?: Record<string, string[]> | null,
    exact?: boolean
  ): Promise<PlacesSchedulePreviewResult> {
    if (!programId) return DISABLED_PREVIEW;
    try {
      const { data } = await client.query({
        query: SCHEDULE_PREVIEW_EXACT_QUERY,
        variables: { programId, availability: availability ?? null, exact: exact ?? false },
        fetchPolicy: 'network-only'
      });
      return (data?.myPlacesSchedulePreview as PlacesSchedulePreviewResult) ?? DISABLED_PREVIEW;
    } catch {
      return DISABLED_PREVIEW;
    }
  }

  /**
   * placesPresenceRoster(programId) — the co-presence "who's here" surface (PW6). Returns
   * who's ON SITE right now (BE-scoped: excludes the caller + opted-out) plus counts.
   * SILENT-DEGRADES to a disabled ({ enabled:false, here:[] }) payload so a missing/failing
   * presence backend renders the graceful-empty / hidden state — never precise data.
   *
   * Exported on the composable so the ecosystem-world "who's at Seeed now" surface (a
   * CROSS-BRANCH follow-up on epic/ecosystem-world) can reuse this exact loader.
   */
  async function loadPlacesPresenceRoster(programId: string): Promise<PlacesPresenceRoster> {
    if (!programId) return DISABLED_PRESENCE;
    try {
      const { data } = await client.query({
        query: PLACES_PRESENCE_ROSTER_QUERY,
        variables: { programId },
        fetchPolicy: 'network-only'
      });
      return (data?.placesPresenceRoster as PlacesPresenceRoster) ?? DISABLED_PRESENCE;
    } catch {
      return DISABLED_PRESENCE;
    }
  }

  /**
   * setPlacesPolicy(input) — the creator's on-site policy write. Returns the resolved
   * module-state payload (mirrors placesModuleState) or null on failure so the caller can
   * surface a save error.
   */
  async function setPlacesPolicy(input: SetPlacesPolicyInput): Promise<PlacesModuleState | null> {
    if (!input?.programId) return null;
    const { data } = await client.mutate({
      mutation: SET_PLACES_POLICY_MUTATION,
      variables: { input }
    });
    return (data?.setPlacesPolicy as PlacesModuleState) ?? null;
  }

  /**
   * propagatePlacesChange(programId, dryRun) — the blast-radius gate. dryRun:true returns
   * the affected-participant preview WITHOUT writing; dryRun:false applies. Returns null on
   * failure. Does NOT silent-degrade to an empty preview — a null lets the caller keep the
   * confirm dialog closed rather than falsely claim "0 affected".
   */
  async function propagatePlacesChange(
    programId: string,
    dryRun: boolean
  ): Promise<PropagatePlacesChangeResult | null> {
    if (!programId) return null;
    try {
      const { data } = await client.mutate({
        mutation: PROPAGATE_PLACES_CHANGE_MUTATION,
        variables: { programId, dryRun }
      });
      return (data?.propagatePlacesChange as PropagatePlacesChangeResult) ?? null;
    } catch {
      return null;
    }
  }

  /**
   * setPlacesPledge(...) — the participant commits their on-site pledge (PW5 schedule-first).
   * The caller (the onboarding phase / Manage re-confirm) passes the edited `sessions[]` and the
   * chosen mode. Returns the TYPED ENVELOPE `{ enabled, ok, reason, contract }` — the caller
   * surfaces the REAL `reason` (`shortfall_blocked` / `not_enrolled` / `error`) instead of a
   * generic "couldn't save". Never returns null; a thrown/absent payload folds to reason:'error'.
   */
  async function setPlacesPledge(input: {
    programId: string;
    mode?: PlacesPledgeMode;
    shape?: PlacesPledgeShape;
    pledgedHours?: number | null;
    pledgedTotalHours?: number | null;
    sessions?: PlacesPledgeSessionInput[];
    availability?: Record<string, string[]> | null;
  }): Promise<SetPlacesPledgeEnvelope> {
    const errorEnvelope: SetPlacesPledgeEnvelope = {
      enabled: false,
      ok: false,
      reason: 'error',
      contract: null
    };
    if (!input?.programId) {
      // eslint-disable-next-line no-console
      console.error('[setPlacesPledge] NO programId — bailing', input);
      return errorEnvelope;
    }
    const variables = {
      programId: input.programId,
      mode: input.mode ?? null,
      shape: input.shape ?? null,
      pledgedHours: input.pledgedHours ?? null,
      pledgedTotalHours: input.pledgedTotalHours ?? null,
      sessions: input.sessions ?? null,
      availability: input.availability ?? null
    };
    try {
      const { data, errors } = (await client.mutate({
        mutation: SET_PLACES_PLEDGE_MUTATION,
        variables,
        errorPolicy: 'all'
      })) as { data?: { setPlacesPledge?: SetPlacesPledgeEnvelope }; errors?: unknown };
      if (!data?.setPlacesPledge) {
        const gqlMessages = Array.isArray(errors)
          ? (errors as Array<{ message?: string }>).map(e => e?.message).join(' | ')
          : String(errors);
        // eslint-disable-next-line no-console
        console.error('[setPlacesPledge] GraphQL error:', gqlMessages);
        return errorEnvelope;
      }
      return data.setPlacesPledge as SetPlacesPledgeEnvelope;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[setPlacesPledge] threw:', err);
      return errorEnvelope;
    }
  }

  /**
   * switchToRemote(programId) — the participant opts out of on-site attendance. Returns the
   * re-resolved remote contract or null on failure.
   */
  async function switchToRemote(programId: string): Promise<SwitchToRemoteResult | null> {
    if (!programId) return null;
    try {
      const { data } = await client.mutate({
        mutation: SWITCH_TO_REMOTE_MUTATION,
        variables: { programId }
      });
      return (data?.switchToRemote as SwitchToRemoteResult) ?? null;
    } catch {
      return null;
    }
  }

  /**
   * syncMyPlacesCalendar(programId) — push the participant's sessions to their calendar.
   * Returns {sent, sessionCount, unchanged} or null on failure so the Manage dialog can
   * toast the outcome.
   */
  async function syncMyPlacesCalendar(programId: string): Promise<SyncPlacesCalendarResult | null> {
    if (!programId) return null;
    try {
      const { data } = await client.mutate({
        mutation: SYNC_MY_PLACES_CALENDAR_MUTATION,
        variables: { programId }
      });
      return (data?.syncMyPlacesCalendar as SyncPlacesCalendarResult) ?? null;
    } catch {
      return null;
    }
  }

  /**
   * reportMissedSession(programId, sessionUid, status, note) — the participant reports a past
   * session they came LATE to or MISSED (PW6). Records the report AND clears the stacking gate
   * on that session — the first-class op that replaces the PW5 check-in-note stopgap. Returns
   * the result ({sessionUid, status, cleared}) or null on failure so the caller can keep the
   * gate up rather than falsely dismiss.
   */
  async function reportMissedSession(
    programId: string,
    sessionUid: string,
    status: ReportMissedStatus,
    note?: string
  ): Promise<ReportMissedSessionResult | null> {
    if (!programId || !sessionUid) return null;
    try {
      const { data } = await client.mutate({
        mutation: REPORT_MISSED_SESSION_MUTATION,
        variables: { programId, sessionUid, status, note: note ?? null }
      });
      return (data?.reportMissedSession as ReportMissedSessionResult) ?? null;
    } catch {
      return null;
    }
  }

  /**
   * setPlacePresenceVisibility(programId, hidden) — the participant's "who's here" opt-out
   * (PW6). `hidden:true` removes them from co-participants' presence rosters; `hidden:false`
   * shows them (default). Returns the persisted flag or null on failure so the toggle can
   * revert on error.
   */
  async function setPlacePresenceVisibility(
    programId: string,
    hidden: boolean
  ): Promise<SetPlacePresenceVisibilityResult | null> {
    if (!programId) return null;
    try {
      const { data } = await client.mutate({
        mutation: SET_PLACE_PRESENCE_VISIBILITY_MUTATION,
        variables: { programId, hidden }
      });
      return (data?.setPlacePresenceVisibility as SetPlacePresenceVisibilityResult) ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Best-effort geolocation options carried through to checkIn — the participant's coords
   * (silent, permission-gated) and/or a scanned QR token. All optional; the check-in
   * NEVER blocks on geo (the runner reads permission best-effort).
   */
  interface CheckInGeo {
    geoLat?: number | null;
    geoLng?: number | null;
    qrToken?: string | null;
  }

  async function checkIn(
    programId: string,
    placeId: string,
    note?: string,
    geo?: CheckInGeo
  ): Promise<boolean> {
    if (!programId || !placeId) return false;
    try {
      await client.mutate({
        mutation: PLACE_CHECK_IN_MUTATION,
        variables: {
          programId,
          placeId,
          note: note ?? null,
          geoLat: geo?.geoLat ?? null,
          geoLng: geo?.geoLng ?? null,
          qrToken: geo?.qrToken ?? null
        }
      });
      return true;
    } catch {
      return false;
    }
  }

  async function checkOut(programId: string, placeId: string): Promise<boolean> {
    if (!programId || !placeId) return false;
    try {
      await client.mutate({
        mutation: PLACE_CHECK_OUT_MUTATION,
        variables: { programId, placeId }
      });
      return true;
    } catch {
      return false;
    }
  }

  /**
   * The policyLoader the shared trigger runner iterates: loads the server module
   * state and maps it into a ModulePolicy (with `state`/`owedWindow` folded in).
   */
  async function loadPolicy(programId: string): Promise<ModulePolicy> {
    const moduleState = await loadPlacesModuleState(programId);
    return mapStateToPolicy(moduleState);
  }

  /**
   * Attach the Places module's capability hooks onto the 'places' descriptor key.
   * Additive + idempotent (registerModuleDescriptor is a replace), behind
   * PROGRAM_MODULES at the call site. Closes over THIS instance's loaders so the
   * policyLoader reuses the already-bound Nexus client.
   */
  function registerDescriptor(): void {
    registerModuleDescriptor('places', {
      // The generic framework consumer mounts this; the host also opens it directly
      // via onModuleSelect('places'). PW5: the manage-in-person GUIDE (progress + pledge +
      // logistics + sync) is the module home. The day-of check-in/out popup is a separate
      // runner-opened surface (PlacesCheckInPopup), not the descriptor panel.
      panel: (() =>
        import(
          '@/components/organisms/places/PlacesManageDialog.vue'
        )) as unknown as () => Promise<Component>,
      policyLoader: loadPolicy,
      nudge: {
        // Pass-through: deriveState runs SERVER-side; we surface `policy.state`.
        deriveState: (policy: ModulePolicy) =>
          (policy.state as 'idle' | 'due' | 'overdue') ?? 'idle'
      },
      onboardingPhase: {
        component: (() =>
          import(
            '@/components/organisms/places/PlacesOnboardingPhase.vue'
          )) as unknown as () => Promise<Component>,
        // Show the phase when the program has an enabled Places module.
        shouldShow: (ctx: unknown) => {
          const c = ctx as { placesEnabled?: boolean; placesAvailable?: boolean } | null;
          return !!(c && (c.placesEnabled || c.placesAvailable));
        }
      }
    });
  }

  return {
    loadPlacesModuleState,
    loadMyPlaceAttendance,
    loadPlacesAttendanceSummary,
    loadMyPlacesContract,
    schedulePreview,
    loadPlacesPresenceRoster,
    setPlacesPolicy,
    setPlacesPledge,
    switchToRemote,
    syncMyPlacesCalendar,
    reportMissedSession,
    setPlacePresenceVisibility,
    propagatePlacesChange,
    checkIn,
    checkOut,
    loadPolicy,
    registerDescriptor,
    mapStateToPolicy
  };
}

export default usePlaces;
